/**
 * @fileoverview TourStatusCard.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom';
import { apiFetch } from '../../utils/api';

function TourStatusCard({ tourID }) {
    const [loading, setLoading] = useState(true);
    const [tourData, setTourData] = useState(null);

    useEffect(() => {
        if (!tourID) return;
        setLoading(true);
        
        // Try fetching from the backend
        apiFetch(`/bookings/track/${tourID}`)
            .then(res => {
                if (res && res.success && res.data) {
                    setTourData(res.data);
                } else {
                    throw new Error('Invalid structure');
                }
            })
            .catch(() => {
                setTourData(null);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [tourID]);

    if (loading) {
        return (
            <div className='w-full bg-white p-8 rounded-[2rem] shadow-2xl border border-gray-100 flex items-center justify-center py-12'>
                <div className='w-8 h-8 border-4 border-KITH-primary/30 border-t-KITH-primary rounded-full animate-spin'></div>
            </div>
        );
    }

    if (!tourData) {
        return (
            <div className='w-full bg-white p-8 rounded-[2rem] shadow-2xl border border-gray-100 flex flex-col gap-6 animate-fadeIn'>
                <div className='flex flex-col items-center gap-4 py-4 text-red-500'>
                    <div className='w-12 h-12 bg-red-50 rounded-full flex items-center justify-center'>
                        <i className='fi fi-br-cross-circle text-xl'></i>
                    </div>
                    <p className='font-bold'>404: Tour Not Found</p>
                    <p className='text-xs text-gray-400 text-center'>Please check your ID and try again.</p>
                </div>
            </div>
        );
    }

    // Determine status progress bar percentage
    let progressPercent = 10;
    switch (tourData.status) {
        case 'pending': progressPercent = 20; break;
        case 'confirmed': progressPercent = 40; break;
        case 'ready': progressPercent = 70; break;
        case 'completed': progressPercent = 100; break;
        case 'cancelled': progressPercent = 0; break;
    }

    return (
        <div className='w-full bg-white p-8 rounded-[2rem] shadow-2xl border border-gray-100 flex flex-col gap-6 animate-fadeIn'>
            <div className='flex flex-col gap-4'>
                <div className='flex justify-between items-center'>
                    <div className='flex flex-col text-left'>
                        <span className='text-[10px] font-bold text-gray-400 uppercase tracking-widest'>Tour Number</span>
                        <span className='text-lg font-bold text-gray-800'>#{tourData.confirmationNumber}</span>
                    </div>
                    <div className='flex flex-col text-right'>
                        <span className='text-[10px] font-bold text-gray-400 uppercase tracking-widest'>Coordinator</span>
                        <span className='text-base font-bold text-KITH-primary'>{tourData.tourCoordinator || 'KITH Travels'}</span>
                    </div>
                </div>
                
                <div className='h-[1px] w-full bg-gray-100'></div>
                
                <div className='flex flex-col gap-3'>
                    <div className='flex justify-between items-end'>
                        <span className='text-sm font-bold text-gray-700 capitalize'>
                            Status: {tourData.status}
                        </span>
                        <span className='text-xs font-bold text-gray-400'>{Math.round(progressPercent)}%</span>
                    </div>
                    <div className='w-full h-3 bg-gray-100 rounded-full overflow-hidden'>
                        <div 
                            className={`h-full rounded-full transition-all duration-1000 ease-out ${tourData.status === 'completed' ? 'bg-green-500' : tourData.status === 'cancelled' ? 'bg-red-500' : 'bg-KITH-primary'}`}
                            style={{ width: `${progressPercent}%` }}
                        ></div>
                    </div>
                </div>
            </div>

            <Link 
                to={`/my-tour/${tourData.confirmationNumber}`} 
                className='w-full py-4 bg-gray-800 text-white font-bold rounded-xl hover:bg-black transition-all flex items-center justify-center gap-2 group'
            >
                <span>View Full Itinerary</span>
                <i className='fi fi-br-arrow-right text-xs transition-transform group-hover:translate-x-1'></i>
            </Link>
        </div>
    );
}

export default TourStatusCard;